/**
 * Refresh-island (§progressive-enhancement): een tab die lang openstond, toont verouderde weerdata
 * ("nu", komende uren, "bijgewerkt om …"). Deze island leest het versheid-stempel uit de pagina
 * (`<meta name="pw-fetched">`, = `freshness.fetchedAt`) en herlaadt de pagina rustig zodra die data
 * te oud is.
 *
 * Puur client-side: de check is een klok-vergelijking, géén endpoint en géén origin-call. De reload
 * zelf haalt verse HTML via Bunny/de gebakken site — dus dit werkt zowel nu (SSR) als straks in het
 * bakkerij-eindbeeld zonder enige extra infra.
 *
 * Rustig betekent: alléén op natuurlijke momenten — wanneer de tab weer in beeld komt
 * (visibilitychange), uit de bfcache terugkomt (pageshow), of een zuinige interval terwijl de tab
 * verbórgen is (dan is de reload onzichtbaar). Nooit midden in het lezen van een zichtbare tab.
 *
 * Een korte herlaad-drempel (RELOAD_GAP_MS, in sessionStorage zodat ze de reload overleeft) voorkomt
 * een herlaad-lus als de CDN-rand vlak na de drempel nog even dezelfde (oude) HTML serveert; zodra de
 * rand vers is, daalt de leeftijd onder de grens en stopt het vanzelf.
 *
 * Zonder JS gebeurt er niets: de pagina werkt verder (de forecast is server-rendered, met een
 * eerlijke "bijgewerkt om"-stempel).
 */

// Toont een tab data ouder dan dit, dan is een reload de moeite waard om verse data te halen
// (eigenaarskeuze: 15 min). De forecast-cache ververst ~elke 30 min, maar bezoekers verspreid over
// de dag houden de edge/cache jonger, dus een reload levert doorgaans iets recenters op.
const MAX_AGE_MS = 15 * 60_000;
const RELOAD_GAP_MS = 2 * 60_000; // niet vaker dan eens per 2 min herladen (lus-rem)
const HIDDEN_POLL_MS = 5 * 60_000; // zuinige achtergrond-controle terwijl de tab verborgen is
const STORAGE_KEY = 'puurweer.laatste-herlaad';

/** Lees de bewaarde herlaad-tijd (sessionStorage; overleeft de reload). 0 = nooit/onbeschikbaar. */
function readLastReload(): number {
  try {
    return Number(sessionStorage.getItem(STORAGE_KEY)) || 0;
  } catch {
    return 0; // sessionStorage uit → terugval op de module-var in initRefresh
  }
}

function writeLastReload(now: number): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(now));
  } catch {
    // sessionStorage uit → niet erg; de module-var dient als terugval binnen deze paginalevensduur
  }
}

/**
 * Pure beslissing (testbaar): herladen als de getoonde data ouder is dan MAX_AGE_MS én de
 * herlaad-drempel verstreken is. Een ongeldige/ontbrekende fetch-tijd → niet herladen.
 */
export function shouldReload(fetchedAt: number, lastReloadAt: number, now: number): boolean {
  if (!Number.isFinite(fetchedAt) || fetchedAt <= 0) return false;
  if (now - fetchedAt < MAX_AGE_MS) return false;
  return now - lastReloadAt >= RELOAD_GAP_MS;
}

export function initRefresh(): void {
  const meta = document.querySelector('meta[name="pw-fetched"]');
  const raw = meta instanceof HTMLMetaElement ? meta.content.trim() : '';
  const fetchedAt = raw ? Date.parse(raw) : Number.NaN;
  if (!Number.isFinite(fetchedAt)) return; // geen (geldig) versheid-stempel → niets te doen

  let memLastReload = 0; // terugval als sessionStorage uit staat (overleeft de reload niet, prima)

  function maybeReload(): void {
    const now = Date.now();
    const lastReloadAt = Math.max(readLastReload(), memLastReload);
    if (!shouldReload(fetchedAt, lastReloadAt, now)) return;
    memLastReload = now;
    writeLastReload(now);
    location.reload();
  }

  // Tab komt weer in beeld — het klassieke "stond nog open"-geval.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') maybeReload();
  });
  // Uit de bfcache hersteld (terug-/vooruit-knop): pageshow met persisted.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) maybeReload();
  });
  // Zuinige interval, maar alléén terwijl de tab verborgen is: een reload blijft dan onzichtbaar en
  // de gebruiker ziet bij terugkomst de verse pagina (nooit een reload midden in het lezen).
  setInterval(() => {
    if (document.hidden) maybeReload();
  }, HIDDEN_POLL_MS);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRefresh);
} else {
  initRefresh();
}
